import React, { useState } from "react";
import { Plus, Trash2, Pencil, Sparkles } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listFunctionTitleSuggestions, createFunctionTitleSuggestion,
  updateFunctionTitleSuggestion, deleteFunctionTitleSuggestion,
  FunctionTitleSuggestion, FunctionTitleSuggestionInput,
} from "../api/api";
import { usePartners } from "../hooks/queries";
import { DISCIPLINES, ERVARING_OPTIONS, PRIORITY_OPTIONS, SALARY_TYPE_OPTIONS } from "../constants";
import Layout from "../components/Layout";
import { toast } from "../lib/toast";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { ChipGroup } from "../components/ui/chip-group";
import { Stepper } from "../components/ui/stepper";
import { DataTable, DataTableColumn } from "../components/ui/data-table";
import { EmptyState } from "../components/ui/empty-state";
import { PriorityBadge } from "../components/VacatureBadges";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";

const NONE = "__none__";
const GLOBAL = "__global__";

function emptyForm(): FunctionTitleSuggestionInput {
  return {
    title: "", partner_id: null, priority: null, experience: null, disciplines: null, locatie: null, aantal_posities: null, opmerkingen: null,
    salary_type: null, salary_min: null, salary_max: null, salary_fixed: null, salary_other: null,
  };
}

const ERVARING_LABEL = Object.fromEntries(ERVARING_OPTIONS.map(o => [o.value, o.label]));

export default function Functietitels() {
  const qc = useQueryClient();
  const { data: partners = [] } = usePartners();
  const { data: suggestions = [], isLoading } = useQuery({
    queryKey: ["function-title-suggestions"],
    queryFn: listFunctionTitleSuggestions,
  });

  const [editingId, setEditingId] = useState<number | "new" | null>(null);
  const [form, setForm] = useState<FunctionTitleSuggestionInput>(emptyForm());
  const [confirmDelete, setConfirmDelete] = useState<FunctionTitleSuggestion | null>(null);

  const upsertMut = useMutation({
    mutationFn: (vars: { id: number | "new"; body: FunctionTitleSuggestionInput }) =>
      vars.id === "new" ? createFunctionTitleSuggestion(vars.body) : updateFunctionTitleSuggestion(vars.id, vars.body),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ["function-title-suggestions"] });
      const previous = qc.getQueryData<FunctionTitleSuggestion[]>(["function-title-suggestions"]);
      qc.setQueryData<FunctionTitleSuggestion[]>(["function-title-suggestions"], (old = []) => {
        if (vars.id === "new") return old;
        const idx = old.findIndex(s => s.id === vars.id);
        if (idx === -1) return old;
        const next = [...old];
        next[idx] = { ...next[idx], ...vars.body } as FunctionTitleSuggestion;
        return next;
      });
      return { previous };
    },
    onError: (err, _vars, context) => {
      if (context?.previous) qc.setQueryData(["function-title-suggestions"], context.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["function-title-suggestions"] }),
    onSuccess: (_data, vars) => {
      setEditingId(null);
      setForm(emptyForm());
      toast(vars.id === "new" ? "Suggestie toegevoegd" : "Suggestie opgeslagen", { kind: "success" });
    },
  });

  const deleteMut = useMutation({
    mutationFn: deleteFunctionTitleSuggestion,
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ["function-title-suggestions"] });
      const previous = qc.getQueryData<FunctionTitleSuggestion[]>(["function-title-suggestions"]);
      qc.setQueryData<FunctionTitleSuggestion[]>(["function-title-suggestions"], (old = []) => old.filter(s => s.id !== id));
      return { previous };
    },
    onError: (err, _id, context) => {
      if (context?.previous) qc.setQueryData(["function-title-suggestions"], context.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["function-title-suggestions"] }),
    onSuccess: () => toast("Suggestie verwijderd", { kind: "success" }),
  });

  function startEdit(s: FunctionTitleSuggestion) {
    setEditingId(s.id);
    setForm({
      title: s.title, partner_id: s.partner_id, priority: s.priority, experience: s.experience,
      disciplines: s.disciplines, locatie: s.locatie, aantal_posities: s.aantal_posities, opmerkingen: s.opmerkingen,
      salary_type: s.salary_type, salary_min: s.salary_min, salary_max: s.salary_max, salary_fixed: s.salary_fixed, salary_other: s.salary_other,
    });
  }

  function startNew() {
    setEditingId("new");
    setForm(emptyForm());
  }

  function save() {
    if (!form.title.trim() || editingId === null) return;
    upsertMut.mutate({ id: editingId, body: { ...form, title: form.title.trim() } });
  }

  const partnerName = (id: number | null) =>
    id ? partners.find(p => p.id === id)?.name ?? `Partner #${id}` : "Globaal";

  const columns: DataTableColumn<FunctionTitleSuggestion>[] = [
    {
      key: "title", header: "Titel", width: "minmax(200px,1.6fr)", sortValue: s => s.title,
      cell: s => <span className="font-bold">{s.title}</span>,
    },
    {
      key: "partner", header: "Partner", width: "minmax(130px,1fr)", sortValue: s => partnerName(s.partner_id),
      cell: s => <span className={s.partner_id ? "text-waves-body" : "text-waves-muted"}>{partnerName(s.partner_id)}</span>,
    },
    {
      key: "priority", header: "Prioriteit", width: "110px",
      cell: s => s.priority ? <PriorityBadge value={s.priority} /> : <span className="text-waves-muted">—</span>,
    },
    {
      key: "experience", header: "Ervaring", width: "minmax(110px,0.9fr)",
      cell: s => <span className="text-sm text-waves-body">{s.experience ? ERVARING_LABEL[s.experience] ?? s.experience : "—"}</span>,
    },
    {
      key: "disciplines", header: "Discipline", width: "minmax(120px,1fr)",
      cell: s => <span className="block truncate text-sm text-waves-body">{s.disciplines?.length ? s.disciplines.join(", ") : "—"}</span>,
    },
    {
      key: "posities", header: "Posities", width: "80px", sortValue: s => s.aantal_posities,
      cell: s => <span className="font-mono text-sm text-waves-body">{s.aantal_posities ?? "—"}</span>,
    },
    {
      key: "actions", header: "", width: "96px", className: "flex justify-end gap-1",
      cell: s => (
        <>
          <Button variant="ghost" size="icon" aria-label="Bewerken" onClick={() => startEdit(s)}>
            <Pencil />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Verwijderen"
            onClick={() => setConfirmDelete(s)}
            disabled={deleteMut.isPending && deleteMut.variables === s.id}
            className="hover:text-status-alert-fg"
          >
            <Trash2 />
          </Button>
        </>
      ),
    },
  ];

  return (
    <Layout
      title="Functiesuggesties"
      subtitle="Beheer autofill-suggesties voor de functietitel"
      actions={<Button onClick={startNew}><Plus /> Nieuwe suggestie</Button>}
    >
      <div className="mx-auto flex max-w-[1240px] flex-col gap-5">
        {!isLoading && suggestions.length === 0 ? (
          <div className="rounded-card border border-waves-line bg-white">
            <EmptyState
              icon={<Sparkles />}
              title="Nog geen functiesuggesties"
              description="Voeg een suggestie toe om velden automatisch in te vullen bij het aanmaken van een vacature."
              action={<Button variant="outline" onClick={startNew}><Plus /> Nieuwe suggestie</Button>}
            />
          </div>
        ) : (
          <DataTable
            columns={columns}
            rows={suggestions}
            rowKey={s => s.id}
            minWidth={860}
            empty={isLoading ? "Laden…" : "Nog geen functiesuggesties."}
          />
        )}
      </div>

      <Dialog open={editingId !== null} onOpenChange={open => { if (!open) setEditingId(null); }}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId === "new" ? "Nieuwe suggestie" : "Suggestie bewerken"}</DialogTitle>
            <DialogDescription>Lege velden worden niet automatisch ingevuld.</DialogDescription>
          </DialogHeader>
          <SuggestionForm form={form} setForm={setForm} partners={partners} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingId(null)}>Annuleren</Button>
            <Button onClick={save} disabled={upsertMut.isPending || !form.title.trim()}>
              {upsertMut.isPending ? "Opslaan…" : "Opslaan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmDelete !== null} onOpenChange={open => { if (!open) setConfirmDelete(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suggestie verwijderen?</DialogTitle>
            <DialogDescription>
              {confirmDelete ? <>Weet je zeker dat je <strong className="text-waves-ink">{confirmDelete.title}</strong> wilt verwijderen?</> : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Annuleren</Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (confirmDelete) deleteMut.mutate(confirmDelete.id);
                setConfirmDelete(null);
              }}
            >
              <Trash2 /> Verwijderen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

interface FormProps {
  form: FunctionTitleSuggestionInput;
  setForm: React.Dispatch<React.SetStateAction<FunctionTitleSuggestionInput>>;
  partners: { id: number; name: string }[];
}

function Field({ label, optional, children }: { label: string; optional?: boolean; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline gap-1.5">
        <span className="text-[13px] font-bold text-waves-ink">{label}</span>
        {optional && <span className="ml-auto text-xs text-waves-muted">geen standaardwaarde als leeg</span>}
      </div>
      {children}
    </div>
  );
}

function SuggestionForm({ form, setForm, partners }: FormProps) {
  return (
    <div className="flex flex-col gap-5">
      <Field label="Titel">
        <Input
          value={form.title}
          onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
          placeholder="Bijv. Monteur gas en water"
        />
      </Field>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label="Partner">
          <Select
            value={form.partner_id != null ? String(form.partner_id) : GLOBAL}
            onValueChange={v => setForm(f => ({ ...f, partner_id: v === GLOBAL ? null : Number(v) }))}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={GLOBAL}>Globaal</SelectItem>
              {partners.map(p => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Ervaring">
          <Select
            value={form.experience ?? NONE}
            onValueChange={v => setForm(f => ({ ...f, experience: v === NONE ? null : v }))}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Geen standaardwaarde</SelectItem>
              {ERVARING_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <Field label="Discipline" optional>
        <ChipGroup
          multiple
          aria-label="Discipline"
          value={form.disciplines ?? []}
          onChange={next => setForm(f => ({ ...f, disciplines: next.length ? next : null }))}
          options={DISCIPLINES.map(d => ({ value: d, label: d }))}
        />
      </Field>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label="Prioriteit" optional>
          <ChipGroup
            aria-label="Prioriteit"
            value={form.priority ?? null}
            onChange={v => setForm(f => ({ ...f, priority: f.priority === v ? null : v }))}
            options={PRIORITY_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
          />
        </Field>

        <Field label="Aantal posities" optional>
          {form.aantal_posities != null ? (
            <div className="flex items-center gap-3">
              <Stepper
                aria-label="Aantal posities"
                value={form.aantal_posities}
                min={1}
                max={99}
                onChange={n => setForm(f => ({ ...f, aantal_posities: n }))}
                className="flex-1"
              />
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, aantal_posities: null }))}
                className="text-[13px] font-bold text-waves-muted hover:text-waves-ink"
              >
                Wissen
              </button>
            </div>
          ) : (
            <Button variant="outline" className="self-start" onClick={() => setForm(f => ({ ...f, aantal_posities: 1 }))}>
              <Plus /> Standaardwaarde instellen
            </Button>
          )}
        </Field>
      </div>

      <Field label="Locatie" optional>
        <Input
          value={form.locatie ?? ""}
          onChange={e => setForm(f => ({ ...f, locatie: e.target.value || null }))}
          placeholder="Stad…"
        />
      </Field>

      <Field label="Salarisindicatie" optional>
        <ChipGroup
          aria-label="Salaristype"
          value={form.salary_type ?? null}
          onChange={v => setForm(f => ({
            ...f,
            salary_type: f.salary_type === v ? null : (v as "range" | "fixed" | "other"),
            salary_min: null, salary_max: null, salary_fixed: null, salary_other: null,
          }))}
          options={SALARY_TYPE_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
        />
        {form.salary_type === "range" && (
          <div className="flex items-center gap-2">
            <Input
              type="number" min="0" step="1" value={form.salary_min ?? ""}
              onChange={e => setForm(f => ({ ...f, salary_min: e.target.value ? Number(e.target.value) : null }))}
              placeholder="Van (€)"
            />
            <span className="shrink-0 text-sm text-waves-muted">tot</span>
            <Input
              type="number" min="0" step="1" value={form.salary_max ?? ""}
              onChange={e => setForm(f => ({ ...f, salary_max: e.target.value ? Number(e.target.value) : null }))}
              placeholder="Tot (€)"
            />
          </div>
        )}
        {form.salary_type === "fixed" && (
          <Input
            type="number" min="0" step="1" value={form.salary_fixed ?? ""}
            onChange={e => setForm(f => ({ ...f, salary_fixed: e.target.value ? Number(e.target.value) : null }))}
            placeholder="Bedrag (€)"
          />
        )}
        {form.salary_type === "other" && (
          <Input
            value={form.salary_other ?? ""}
            onChange={e => setForm(f => ({ ...f, salary_other: e.target.value || null }))}
            placeholder="Bijv. marktconform"
          />
        )}
      </Field>

      <Field label="Omschrijving / opmerkingen" optional>
        <Textarea
          rows={3}
          value={form.opmerkingen ?? ""}
          onChange={e => setForm(f => ({ ...f, opmerkingen: e.target.value || null }))}
          placeholder="Extra context, vereisten of opmerkingen…"
        />
      </Field>
    </div>
  );
}
